import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errText, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const ProfileCard = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    agency_name: user?.agency_name || "",
    rera_id: user?.rera_id || "",
    about: user?.about || "",
    picture: user?.picture || null,
  });
  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phone: user.phone || "",
      agency_name: user.agency_name || "",
      rera_id: user.rera_id || "",
      about: user.about || "",
      picture: user.picture || null,
    });
  }, [user]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, picture: data.file_id }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="rounded-2xl border border-border p-6 md:p-8 space-y-4" data-testid="profile-card">
      <div>
        <h3 className="font-display text-lg">My profile</h3>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Buyers see your name, photo and agency details on your listings.
        </p>
      </div>

      <div className="flex items-center gap-4">
        {form.picture ? (
          <img src={imageUrl(form.picture)} alt="Profile" className="h-16 w-16 rounded-full object-cover" data-testid="profile-photo" />
        ) : (
          <div className="grid h-16 w-16 place-items-center rounded-full bg-muted text-lg font-bold">
            {(form.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-4 py-2.5 text-[13px] font-semibold hover:border-foreground/30 transition-colors duration-200">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {form.picture ? "Change photo" : "Upload photo"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="profile-photo-input" />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Full name</Label>
          <Input value={form.name} onChange={set("name")} className="h-12 rounded-lg" data-testid="profile-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Mobile number</Label>
          <Input value={form.phone} onChange={set("phone")} inputMode="tel" placeholder="10-digit mobile" className="h-12 rounded-lg" data-testid="profile-phone-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Agency / company (optional)</Label>
          <Input value={form.agency_name} onChange={set("agency_name")} className="h-12 rounded-lg" data-testid="profile-agency-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">RERA id (optional)</Label>
          <Input value={form.rera_id} onChange={set("rera_id")} className="h-12 rounded-lg" data-testid="profile-rera-input" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">About you (optional)</Label>
        <Textarea value={form.about} onChange={set("about")} rows={3} className="rounded-lg" placeholder="Areas you cover, years of experience…" data-testid="profile-about-input" />
      </div>

      <Button type="submit" disabled={busy} className="h-12 rounded-lg font-bold hover:brightness-95" data-testid="profile-save-btn">
        {busy ? "Saving…" : "Save profile"}
      </Button>
    </form>
  );
};

export default ProfileCard;
